import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { wikiApi } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { WikiMarkdown } from '@/components/wiki/WikiMarkdown';
import { parseWikiContent } from '@/lib/wiki-content';

export function WikiArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['wiki', slug],
    queryFn: () => wikiApi.get(slug!),
    enabled: Boolean(slug),
    staleTime: 60_000,
  });

  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-12">
      <Link
        to="/wiki"
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-eol-desc transition hover:text-eol-pink"
      >
        <ArrowLeft className="h-4 w-4" />
        Все статьи
      </Link>

      {isLoading ? (
        <p className="text-eol-desc">Загрузка…</p>
      ) : isError || !data ? (
        <Card>
          <CardContent className="py-10 text-center">
            <BookOpen className="mx-auto mb-3 h-9 w-9 text-eol-pink" />
            <h1 className="text-2xl font-bold text-eol-white">Статья не найдена</h1>
            <p className="mt-2 text-eol-desc">Возможно, она была скрыта или удалена.</p>
          </CardContent>
        </Card>
      ) : (
        <article>
          <h1 className="text-3xl font-extrabold text-eol-white md:text-5xl">{data.title}</h1>
          <p className="mt-4 text-lg leading-8 text-eol-desc">{data.summary}</p>
          <Card className="mt-8">
            <CardContent className="space-y-7 px-6 py-7 md:px-8">
              {parseWikiContent(data.content).map((block) =>
                block.type === 'text' ? (
                  <WikiMarkdown key={block.id} content={block.text} />
                ) : (
                  <figure key={block.id}>
                    <img
                      src={block.url}
                      alt={block.caption}
                      loading="lazy"
                      className="max-h-[640px] w-full rounded-xl border border-white/5 object-cover shadow-lg"
                    />
                    {block.caption && (
                      <figcaption className="mt-2 text-center text-sm text-eol-desc">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                ),
              )}
            </CardContent>
          </Card>
        </article>
      )}
    </div>
  );
}
